/**
 * File operations using the new MpRawMode infrastructure.
 *
 * Each operation acquires the device mutex, enters raw REPL, performs the operation,
 * exits raw REPL, and releases the mutex — all handled by MpRawMode.begin()/end().
 *
 * These functions replace the old fileBrowser.ts implementations that used
 * executeCommand + custom markers (START_CONTENT/END_CONTENT, SUCCESS/ERROR:).
 */
import type {
    EditorContext,
    FileCache,
    VerifiedReadResult,
    VerifiedWriteResult,
} from '@pyranger/types';
import {
    MAX_VERIFICATION_RETRIES,
    HASH_COMPUTATION_TIMEOUT_MS,
} from '@pyranger/constants';

import { computeFileHash, updateHashEntry } from './fileIntegrity';

import { MpRawMode } from './mpRawMode';
import {
    getFilesystemScript,
    DEVICE_INFO_SCRIPT,
    READ_ONLY_CHECK_SCRIPT,
    getReadFileScript,
    getEnsureDirScript,
    getCreateDirScript,
    getDeleteTreeScript,
    generateSingleFileHashScript,
    parseSingleHashResponse,
} from './deviceScripts';

/**
 * Escape a file path for safe inclusion in a Python string literal.
 * Handles backslashes and single quotes.
 */
function escapePath(path: string): string {
    return path.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

// eslint-disable-next-line no-control-regex
const CONTROL_CHAR_RE = /[\x00-\x1f\x7f]/;

/**
 * Assert that a path is safe for device operations.
 * Throws if the path contains traversal sequences or control characters.
 * This is a defense-in-depth check — UI components should validate first.
 */
function assertSafePath(path: string): void {
    if (!path || path.trim().length === 0) {
        throw new Error('File path cannot be empty');
    }
    if (CONTROL_CHAR_RE.test(path)) {
        throw new Error(
            'File path cannot contain control characters (bytes 0x00–0x1F, 0x7F)',
        );
    }
    const segments = path.split('/');
    for (const segment of segments) {
        if (segment === '..') {
            throw new Error(
                'File path cannot contain ".." (directory traversal)',
            );
        }
    }
}

/**
 * Decode a base64-encoded string to UTF-8 text.
 * Handles multi-line base64 output (strips whitespace before decoding).
 */
function base64ToString(b64: string): string {
    const cleaned = b64.replace(/\s/g, '');
    if (cleaned.length === 0) return '';

    const binary = atob(cleaned);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new TextDecoder().decode(bytes);
}

/**
 * List the device filesystem and return a FileCache structure.
 *
 * Executes a Python script that recursively walks the filesystem and prints
 * a line-delimited format: `f|/path|size` for files, `d|/path/|0` for directories.
 */
export async function listFilesystem({
    context,
    src = '',
}: {
    context: EditorContext;
    src?: string;
}): Promise<FileCache> {
    const raw = await MpRawMode.begin(context);
    try {
        const result = await raw.exec(
            getFilesystemScript(src ? escapePath(src) : ''),
            15000,
        );

        return parseFileList(result);
    } finally {
        await raw.end();
    }
}

/**
 * Parse the line-delimited filesystem output into a FileCache.
 */
function parseFileList(output: string): FileCache {
    const cache: FileCache = {};

    for (const line of output.split('\n')) {
        const trimmed = line.trim();
        if (!trimmed) continue;

        const parts = trimmed.split('|');
        if (parts.length < 3) continue;

        const [type, path, sizeStr] = parts;
        const size = parseInt(sizeStr, 10) || 0;

        if (type === 'f') {
            cache[path] = { type: 'file', size };
        } else if (type === 'd') {
            cache[path] = { type: 'directory', size: 0 };
        }
    }

    return cache;
}

/**
 * Read a file from the device and return its content as a string.
 *
 * Uses base64 encoding for safe transfer over the text-based REPL (33% overhead).
 * Reads in 192-byte chunks (produces 256 base64 chars per line).
 */
export async function readFileFromDevice({
    context,
    path,
}: {
    context: EditorContext;
    path: string;
}): Promise<string> {
    assertSafePath(path);
    const raw = await MpRawMode.begin(context);
    try {
        const b64 = await raw.exec(getReadFileScript(escapePath(path)), 30000);

        return base64ToString(b64);
    } finally {
        await raw.end();
    }
}

/**
 * Write a file to the device.
 *
 * Splits content into chunks and writes each via a separate exec() call
 * to avoid MCU memory pressure. Uses base64 encoding for safe transfer.
 */
export async function writeFileToDevice({
    context,
    path,
    content,
}: {
    context: EditorContext;
    path: string;
    content: string;
}): Promise<{ size: number }> {
    assertSafePath(path);
    const raw = await MpRawMode.begin(context);
    try {
        // Ensure parent directories exist
        const dirParts = path.split('/').slice(0, -1);
        if (dirParts.length > 0) {
            await raw.exec(getEnsureDirScript(escapePath(dirParts.join('/'))));
        }

        // Open file for writing and import binascii
        await raw.exec(`import binascii\nf=open('${escapePath(path)}','wb')`);

        // Write content in chunks (base64-encoded)
        const encoded = new TextEncoder().encode(content);
        const CHUNK_SIZE = 192; // 192 bytes → 256 base64 chars

        for (let i = 0; i < encoded.length; i += CHUNK_SIZE) {
            const chunk = encoded.slice(i, i + CHUNK_SIZE);
            const b64 = btoa(String.fromCharCode(...chunk));
            await raw.exec(`f.write(binascii.a2b_base64('${b64}'))`);
        }

        // Close file and get size
        const sizeResult = await raw.exec(
            `f.close()
import os
print(os.stat('${escapePath(path)}')[6])
`,
        );

        const size = parseInt(sizeResult.trim(), 10) || content.length;

        return { size };
    } finally {
        await raw.end();
    }
}

/**
 * Delete a file from the device.
 */
export async function deleteFileOnDevice({
    context,
    path,
}: {
    context: EditorContext;
    path: string;
}): Promise<void> {
    assertSafePath(path);
    const raw = await MpRawMode.begin(context);
    try {
        await raw.exec(`import os;os.remove('${escapePath(path)}')`);
    } finally {
        await raw.end();
    }
}

/**
 * Create a directory on the device, including parent directories.
 */
export async function createDirectoryOnDevice({
    context,
    dirname,
}: {
    context: EditorContext;
    dirname: string;
}): Promise<void> {
    assertSafePath(dirname);
    const raw = await MpRawMode.begin(context);
    try {
        await raw.exec(getCreateDirScript(escapePath(dirname)));
    } finally {
        await raw.end();
    }
}

/**
 * Recursively delete a directory and all its contents from the device.
 */
export async function deleteDirectoryOnDevice({
    context,
    path,
}: {
    context: EditorContext;
    path: string;
}): Promise<void> {
    assertSafePath(path);
    const raw = await MpRawMode.begin(context);
    try {
        await raw.exec(getDeleteTreeScript(escapePath(path)));
    } finally {
        await raw.end();
    }
}

/**
 * Check if a CircuitPython device filesystem is in read-only mode.
 * @returns true if read-only, false if writable
 */
export async function checkReadOnly({
    context,
}: {
    context: EditorContext;
}): Promise<boolean> {
    const raw = await MpRawMode.begin(context);
    try {
        const result = await raw.exec(READ_ONLY_CHECK_SCRIPT);
        return result.trim() === 'True';
    } finally {
        await raw.end();
    }
}

/**
 * Load device information (language, version, machine name, etc.)
 */
export async function loadDeviceInfoFromDevice({
    context,
}: {
    context: EditorContext;
}): Promise<DeviceInfoResult> {
    const raw = await MpRawMode.begin(context);
    try {
        const result = await raw.exec(DEVICE_INFO_SCRIPT, 5000);

        const parts = result.trim().split('|');
        const [machine, release, sysname, language] = parts;

        return {
            machine: machine || 'Unknown',
            release: release || '',
            sysname: sysname || '',
            language:
                (language as 'MicroPython' | 'CircuitPython') || 'MicroPython',
        };
    } finally {
        await raw.end();
    }
}

export interface DeviceInfoResult {
    machine: string;
    release: string;
    sysname: string;
    language: 'MicroPython' | 'CircuitPython';
}

/**
 * Write a file to the device with SHA-256 integrity verification.
 *
 * Performs write and hash verification within a single MpRawMode session to
 * avoid the overhead of acquiring/releasing the mutex and entering/exiting
 * raw REPL twice per attempt. Retries up to `maxRetries` times on mismatch
 * or communication errors.
 *
 * On success, updates the File_Hash_Cache so subsequent sync checks know the
 * expected hash for this file.
 */
export async function writeFileWithVerification(params: {
    context: EditorContext;
    path: string;
    content: string;
    maxRetries?: number;
}): Promise<VerifiedWriteResult> {
    const {
        context,
        path,
        content,
        maxRetries = MAX_VERIFICATION_RETRIES,
    } = params;

    // 1. Compute browser-side hash
    const browserHash = await computeFileHash(content);

    let lastDeviceHash: string | undefined;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        // Each attempt gets a single raw REPL session for both write + hash
        const raw = await MpRawMode.begin(context);
        try {
            // 2. Ensure parent directories exist
            const escapedPath = escapePath(path);
            const dirParts = path.split('/').slice(0, -1);
            if (dirParts.length > 0) {
                await raw.exec(
                    getEnsureDirScript(escapePath(dirParts.join('/'))),
                );
            }

            // 3. Open file and write content in chunks (base64-encoded)
            await raw.exec(`import binascii\nf=open('${escapedPath}','wb')`);

            const encoded = new TextEncoder().encode(content);
            const CHUNK_SIZE = 192; // 192 bytes → 256 base64 chars

            for (let i = 0; i < encoded.length; i += CHUNK_SIZE) {
                const chunk = encoded.slice(i, i + CHUNK_SIZE);
                const b64 = btoa(String.fromCharCode(...chunk));
                await raw.exec(`f.write(binascii.a2b_base64('${b64}'))`);
            }

            // 4. Close file and get size
            const sizeResult = await raw.exec(
                `f.close()\nimport os\nprint(os.stat('${escapedPath}')[6])`,
            );
            const size = parseInt(sizeResult.trim(), 10) || content.length;

            // 5. Compute device-side hash in the same session
            let deviceHash: string | null = null;
            try {
                const hashOutput = await raw.exec(
                    generateSingleFileHashScript(path),
                    HASH_COMPUTATION_TIMEOUT_MS,
                );
                deviceHash = parseSingleHashResponse(hashOutput);
            } catch {
                deviceHash = null;
            }

            lastDeviceHash = deviceHash ?? undefined;

            if (deviceHash === null) {
                // Device hash computation failed — treat as communication error
                if (attempt === maxRetries) {
                    return {
                        success: false,
                        size,
                        attempts: attempt,
                        browserHash,
                        deviceHash: undefined,
                        error: 'communication-error',
                    };
                }
                continue;
            }

            // 6. Compare hashes
            if (browserHash === deviceHash) {
                // 7. Match: update hash cache
                const portInfo = context.ref.current.connection.port?.getInfo();
                if (
                    portInfo?.usbVendorId != null &&
                    portInfo?.usbProductId != null
                ) {
                    const deviceKey = `${portInfo.usbVendorId}:${portInfo.usbProductId}`;
                    updateHashEntry(deviceKey, path, browserHash);
                }

                return {
                    success: true,
                    size,
                    attempts: attempt,
                    browserHash,
                    deviceHash,
                };
            }

            // 8. Mismatch: retry if attempts remain
            if (attempt === maxRetries) {
                return {
                    success: false,
                    size,
                    attempts: attempt,
                    browserHash,
                    deviceHash,
                    error: 'integrity-mismatch',
                };
            }
        } catch {
            // Communication error
            if (attempt === maxRetries) {
                return {
                    success: false,
                    size: 0,
                    attempts: attempt,
                    browserHash,
                    deviceHash: lastDeviceHash,
                    error: 'communication-error',
                };
            }
        } finally {
            await raw.end();
        }
    }

    // Should not reach here, but handle as max-retries
    return {
        success: false,
        size: 0,
        attempts: maxRetries,
        browserHash,
        deviceHash: lastDeviceHash,
        error: 'max-retries',
    };
}

/**
 * Read a file from the device with SHA-256 integrity verification.
 *
 * Performs device-side hash computation and file read within a single
 * MpRawMode session to avoid the overhead of acquiring/releasing the mutex
 * and entering/exiting raw REPL twice per attempt. Retries up to `maxRetries`
 * times on mismatch or communication errors.
 *
 * If device hash computation fails within the session, proceeds without
 * pre-transfer verification (graceful fallback) and still returns the content
 * with a 'device-hash-unavailable' indicator.
 *
 * On success, updates the File_Hash_Cache.
 */
export async function readFileWithVerification(params: {
    context: EditorContext;
    path: string;
    maxRetries?: number;
}): Promise<VerifiedReadResult> {
    const { context, path, maxRetries = MAX_VERIFICATION_RETRIES } = params;

    assertSafePath(path);

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        const raw = await MpRawMode.begin(context);
        try {
            // 1. Compute device-side hash pre-transfer (in same session)
            let deviceHash: string | null = null;
            try {
                const hashOutput = await raw.exec(
                    generateSingleFileHashScript(path),
                    HASH_COMPUTATION_TIMEOUT_MS,
                );
                deviceHash = parseSingleHashResponse(hashOutput);
            } catch {
                deviceHash = null;
            }

            // 2. Read file content via base64 (in same session)
            const b64 = await raw.exec(
                getReadFileScript(escapePath(path)),
                30000,
            );
            const content = base64ToString(b64);

            // 3. Compute browser-side hash of received content
            const browserHash = await computeFileHash(content);

            // 4. Graceful fallback if device hash computation failed
            if (deviceHash === null) {
                // Can't verify but still return the content
                // Update hash cache with browser-computed hash (best effort)
                const portInfo = context.ref.current.connection.port?.getInfo();
                if (
                    portInfo?.usbVendorId != null &&
                    portInfo?.usbProductId != null
                ) {
                    const deviceKey = `${portInfo.usbVendorId}:${portInfo.usbProductId}`;
                    updateHashEntry(deviceKey, path, browserHash);
                }

                return {
                    success: true,
                    content,
                    hash: browserHash,
                    error: 'device-hash-unavailable',
                };
            }

            // 5. Compare browser hash against device pre-transfer hash
            if (browserHash === deviceHash) {
                // Match: content verified, update hash cache
                const portInfo = context.ref.current.connection.port?.getInfo();
                if (
                    portInfo?.usbVendorId != null &&
                    portInfo?.usbProductId != null
                ) {
                    const deviceKey = `${portInfo.usbVendorId}:${portInfo.usbProductId}`;
                    updateHashEntry(deviceKey, path, browserHash);
                }

                return {
                    success: true,
                    content,
                    hash: browserHash,
                };
            }

            // 6. Mismatch: transfer corruption, retry if attempts remain
            if (attempt === maxRetries) {
                return {
                    success: false,
                    content,
                    hash: browserHash,
                    error: 'integrity-mismatch',
                };
            }
        } catch {
            // Communication error
            if (attempt === maxRetries) {
                return {
                    success: false,
                    content: '',
                    hash: '',
                    error: 'communication-error',
                };
            }
        } finally {
            await raw.end();
        }
    }

    // Should not reach here, but fallback
    return {
        success: false,
        content: '',
        hash: '',
        error: 'communication-error',
    };
}
